import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "./api";
import { trackSignup, trackLogin } from "./analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id first.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        refresh().finally(() => setLoading(false));
    }, [refresh]);

    const loginWithEmail = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("rc_jwt", data.token);
        setUser(data.user);
        trackLogin("email");
        return data.user;
    };

    const signup = async (email, password, name) => {
        const { data } = await api.post("/auth/signup", { email, password, name });
        localStorage.setItem("rc_jwt", data.token);
        setUser(data.user);
        trackSignup("email");
        return data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* noop */ }
        localStorage.removeItem("rc_jwt");
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, setUser, refresh, loginWithEmail, signup, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be inside AuthProvider");
    return ctx;
}
